import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useModal } from '@steelbuy/modal-dialog';
import {
    Notification,
    NotificationLevel,
    toaster,
    useWizardStepsContext,
    WizardBarItemStatus,
} from '@steelbuy/ui-primitive';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { DeleteBulkListingModal } from './DeleteBulkListingModal';
import { EditModal, SUCCESSFUL_TOAST_ID } from './EditModal';
import { RFQTeaser } from '../../create-rfq/rfq-teaser/RFQTeaser';
import { WizardActionBar } from '../../wizard-action-bar/WizardActionBar';
import { useBulkListingContext } from '../bulk-listing-context/BulkListingContext';
import { BulkListingTable } from '../bulk-listing-table/BulkListingTable';
import { BulkListingStep } from '../BulkListing.enums';
import {
    BulkListing,
    BulkUploadForm,
    getFilteredHeadings,
    getIncompleteListings,
    getListingsGrouped,
    getStepRoute,
    GroupedListings,
    isValidListing,
} from '../bulkListingUtil';

import './EditListing.scss';

const LISTINGS_FIELD = 'listings';

const getIndexForListing = (groupedListings: GroupedListings[], tableIndex: number, rowIndex: number) => {
    let index = 0;
    for (let i = 0; i < tableIndex; i += 1) {
        index += groupedListings[i].listings.length;
    }
    return index + rowIndex;
};

const getListingByIndex = (groupedListings: GroupedListings[], index: number) => {
    let counter = 0;
    for (let i = 0; i < groupedListings.length; i += 1) {
        for (let j = 0; j < groupedListings[i].listings.length; j += 1) {
            if (counter === index) {
                return groupedListings[i].listings[j];
            }
            counter += 1;
        }
    }
    return null;
};

export const EditListingsStep = () => {
    const { draftId = '' } = useParams();
    const navigate = useNavigate();
    const [selectedListingIndex, setSelectedListingIndex] = useState(0);
    const { state } = useLocation();

    const editModal = useModal();
    const deleteModal = useModal();
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const { updateStepStatus } = useWizardStepsContext();
    const { control, getValues, reset, setValue } = useFormContext();
    const { fields, remove } = useFieldArray({
        name: LISTINGS_FIELD,
        control,
    });
    const {
        handleSaveDraft,
        updateBulkUploadMutation: { isLoading },
        addEditedListing,
        addDeletedListing,
        setShowUploadCompleteNotification,
        showUploadCompleteNotification,
    } = useBulkListingContext();
    const showUploadNotification = useRef(showUploadCompleteNotification);
    const isQualityCategoriesSupported = useIsVendorFeatureSupported('NON_PRIME_CATEGORIES');

    const listingsData = fields as BulkListing[];
    const formData = getValues();

    const groupedListings = getListingsGrouped(formData as BulkUploadForm, listingsData);
    useEffect(() => {
        if (listingsData.length === 0 && !draftId) {
            reset();
            updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.EMPTY);
            navigate(getStepRoute(1), { replace: true });
        }
        setShowUploadCompleteNotification(false);
    }, [listingsData.length]);

    const onUpdate = useCallback(
        (listingId: string, updatedData: BulkListing) => {
            const currentValues = getValues(LISTINGS_FIELD) || [];
            const newValues = [...currentValues];
            const index = currentValues.findIndex((listing: { listingId: string }) => listing.listingId === listingId);
            newValues[index] = updatedData;
            setValue(LISTINGS_FIELD, newValues, { shouldValidate: true, shouldDirty: true });
        },
        [control, getValues, setValue]
    );

    if (listingsData.length === 0) {
        return null;
    }

    const incompleteListings = getIncompleteListings(listingsData as BulkListing[], isQualityCategoriesSupported);
    const incompleteListingsCount = incompleteListings.length;
    const invalidListings = listingsData.filter((listing) => !isValidListing(listing));
    const invalidListingsCount = invalidListings.length;
    const reviewCount = new Set([
        ...incompleteListings.map((item) => item.id),
        ...invalidListings.map((item) => item.id),
    ]).size;
    const isNextDisabled = incompleteListingsCount !== 0 || invalidListingsCount > 0;

    const materialData = {
        materialType: getValues('materialType'),
        product: getValues('product'),
        prime: getValues('prime'),
    };

    return (
        <div className="bulk-listing-import">
            <p className="bulk-listing-import-listing__title edit-listing-step__title">
                {t('application.createBulkListing.editListings.header', { count: listingsData.length })}
            </p>
            {showUploadNotification.current && (
                <Notification
                    closeButton
                    stayOpen
                    level={NotificationLevel.SUCCESS}
                    message={t('application.createBulkListing.editListings.successMessage')}
                />
            )}
            <Notification
                stayOpen
                level={NotificationLevel.INFO}
                message={t('application.createBulkListing.editListings.summaryMessage', {
                    uploadedCount: listingsData.length,
                    incompleteCount: reviewCount,
                })}
            />
            {groupedListings.map((listingGroup, tableIndex) => (
                <div className="bulk-listing-import-group-item" key={listingGroup.groupValue}>
                    <RFQTeaser
                        material={{ ...materialData, [listingGroup.groupedBy]: listingGroup.groupValue }}
                        showQuality={!isQualityCategoriesSupported}
                    />
                    <BulkListingTable
                        listingsData={listingGroup.listings}
                        onRowClick={(rowIndex) => {
                            setSelectedListingIndex(getIndexForListing(groupedListings, tableIndex, rowIndex));
                            toast.dismiss(SUCCESSFUL_TOAST_ID);
                            editModal.show();
                        }}
                        showCompleteness
                        tableHeadings={getFilteredHeadings({
                            ...formData,
                            [listingGroup.groupedBy]: listingGroup.groupValue,
                        } as BulkUploadForm)}
                        formData={formData as BulkUploadForm}
                        onDelete={(rowIndex) => {
                            setSelectedListingIndex(getIndexForListing(groupedListings, tableIndex, rowIndex));
                            deleteModal.show();
                        }}
                    />
                </div>
            ))}
            <WizardActionBar
                onNext={() => {
                    updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.SUCCESS);
                    navigate(getStepRoute(4, draftId), {
                        state: {
                            fromWizard: true,
                            ...state,
                            showSuccessNotification: false,
                        },
                    });
                }}
                isSubmitDisabled={isNextDisabled}
                nextLabel={t('application.createRFQ.wizardNextButtonLabel')}
                isLoading={isLoading}
                onBack={() => {
                    updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.EMPTY);
                    navigate(getStepRoute(2), { replace: true });
                }}
                onSaveDraft={() => handleSaveDraft(draftId)}
            />
            <EditModal
                modalAccessor={editModal}
                index={selectedListingIndex}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                materialData={getListingByIndex(groupedListings, selectedListingIndex) as any}
                update={onUpdate}
                onNext={() => {
                    const nextIndex = selectedListingIndex === listingsData.length - 1 ? 0 : selectedListingIndex + 1;
                    setSelectedListingIndex(nextIndex);
                }}
                totalCount={listingsData.length}
                onDelete={() => {
                    editModal.hide();
                    deleteModal.show();
                }}
                isLastListing={selectedListingIndex === listingsData.length - 1}
                bulkUploadFormData={formData}
                addEditedListing={addEditedListing}
            />
            <DeleteBulkListingModal
                modalAccessor={deleteModal}
                onConfirm={() => {
                    const listingForIndex = getListingByIndex(groupedListings, selectedListingIndex);
                    if (listingForIndex === null) {
                        return;
                    }
                    const index = listingsData.findIndex((listing) => listing.listingId === listingForIndex.listingId);
                    remove(index);
                    addDeletedListing(listingForIndex.listingId);
                    deleteModal.hide();
                    toaster.success(t('application.createBulkListing.editListings.listingDeleted'));
                }}
            />
        </div>
    );
};
